/**
 * Route map for public SEO pages.
 * Maps a canonical page key to its localized slug per language.
 *
 * HARDENING V1 (LOT 4) — SEO_META et absoluteUrl() supprimés : les
 * métadonnées publiques ont une SSOT unique (PublicSeoDefaults + SEOHead de
 * page via SeoOwnership). absoluteUrl() construisait des canonicals sur
 * window.location.origin — les snapshots prerender servaient
 * http://127.0.0.1:8099/… comme canonical aux moteurs.
 */

export const PUBLIC_ROUTES = {
  home:       { fr: '/',                    en: '/' },
  about:      { fr: '/a-propos',            en: '/about' },
  team:       { fr: '/notre-equipe',        en: '/our-team' },
  howItWorks: { fr: '/comment-ca-marche',   en: '/how-it-works' },
  contact:    { fr: '/contact',             en: '/contact' },
  requestAccess: { fr: '/demander-acces',   en: '/request-access' },
  projectOwners: { fr: '/porteurs-de-projet', en: '/project-owners' },
  login:      { fr: '/connexion',           en: '/login' },
  mentionsLegales: { fr: '/mentions-legales', en: '/legal-notice' },
  privacy: { fr: '/politique-de-confidentialite', en: '/privacy-policy' },
  cookies: { fr: '/politique-de-cookies', en: '/cookie-policy' },
  terms: { fr: '/conditions-generales', en: '/terms-of-use' },
  riskDisclosure: { fr: '/avertissement-risques', en: '/risk-disclosure' },
  createSpv: { fr: '/creer-spv', en: '/create-spv' },
  createSpvIntake: { fr: '/creer-spv/intake', en: '/create-spv/intake' },
  journal: { fr: '/journal', en: '/journal' },
  realisations: { fr: '/realisations', en: '/track-record' },
  // PUBLIC V2.1 — pages piliers « Implantations » (ensemble fermé)
  implantationBali: { fr: '/implantations/bali', en: '/locations/bali' },
  implantationDubai: { fr: '/implantations/dubai', en: '/locations/dubai' },
  implantationAndorre: { fr: '/implantations/andorre', en: '/locations/andorra' },
  implantationAlgarve: { fr: '/implantations/algarve', en: '/locations/algarve' },
  implantationSudFrance: { fr: '/implantations/sud-de-la-france', en: '/locations/south-of-france' },
};

/**
 * Given a pathname like "/fr/a-propos", returns { lang, pageKey }.
 * Returns null if the path doesn't match a known public route.
 */
export function resolvePublicRoute(pathname) {
  const clean = pathname.replace(/\/$/, '') || '/';

  for (const [pageKey, slugs] of Object.entries(PUBLIC_ROUTES)) {
    for (const [lang, slug] of Object.entries(slugs)) {
      const fullPath = `/${lang}${slug === '/' ? '' : slug}`;
      if (clean === fullPath) {
        return { lang, pageKey };
      }
    }
  }
  return null;
}

/**
 * Build a full localized path for a given page key and language.
 */
export function localizedPath(pageKey, lang) {
  const slug = PUBLIC_ROUTES[pageKey]?.[lang];
  if (!slug) return `/${lang}`;
  return `/${lang}${slug === '/' ? '' : slug}`;
}

/**
 * Given the current pathname and new target language,
 * return the equivalent path in the target language.
 */
export function switchLanguagePath(currentPathname, targetLang) {
  const resolved = resolvePublicRoute(currentPathname);
  if (resolved) {
    return localizedPath(resolved.pageKey, targetLang);
  }
  // PHASE 8 B4 — les articles du journal partagent le même slug fr/en :
  // on conserve le chemin et on ne change que le préfixe de langue.
  const journalMatch = currentPathname.match(/^\/(fr|en)(\/journal\/.+)$/);
  if (journalMatch) {
    return `/${targetLang}${journalMatch[2]}`;
  }
  // PHASE 8 B6 — pages réalisations : segment localisé + slug partagé.
  const realisationMatch = currentPathname.match(/^\/(fr|en)\/(realisations|track-record)\/(.+)$/);
  if (realisationMatch) {
    return `/${targetLang}/${targetLang === 'fr' ? 'realisations' : 'track-record'}/${realisationMatch[3]}`;
  }
  // Fallback: just swap the prefix
  return `/${targetLang}`;
}
